import React, { useEffect, useState } from "react";
const STORAGE_KEY = "rulesOfTheRoadAccepted";

const RulesOfTheRoadModal: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem(STORAGE_KEY);
    if (!accepted) {
      setVisible(true);
      document.body.classList.add("overflow-hidden");
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setVisible(false);
    document.body.classList.remove("overflow-hidden");
  };

  if (!visible) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-panel relative w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Fixed Header */}
        <div className="flex items-center justify-between px-8 pt-6 pb-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold title-base-color">
            Rules of the Road of Legal Insight Monitor
          </h2>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto px-8 py-6 flex-1 min-h-0">
          {/* Key Features */}
          <Section title="Key Features">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                Monitors and ingests legislative updates, court decisions and
                other official sources from defined regulators into a single Tax
                & Legal research environment
              </li>
              <li>
                Provides search and filtering (e.g. country, regulator, date,
                classes, keywords, tags, judges) and AI-powered chat with
                documents
              </li>
              <li>
                Generates AI-assisted summaries, classifications, keywords and
                multilingual outputs to speed up review of legislation updates
                and judgments
              </li>
              <li>
                Enables team collaboration via shared tags, prompt libraries,
                comments and team-specific workspaces
              </li>
              <li>
                Allows export of selected results and supports
                newsletters/alerts for internal monitoring
              </li>
            </ul>
          </Section>

          {/* Approved Data */}
          <Section title="Approved Data">
            <p>
              The tool should be used only with generic or hypothetical
              descriptions of legal issues that are not identifying specific
              client, engagement, or matter. Do not input or upload
              client-confidential, personal, client-identifiable, or other
              non-public data, as such information is not required for the tool's
              intended purpose.
            </p>
          </Section>

          {/* Key Limitations */}
          <Section title="Key Limitations">
            <p>
              The tool is currently available only for the Poland, Czech
              Republic, Hungary and Romania member firms, and the onboarded
              regulator sources are restricted to these countries and to the
              specific regulators included in the tool.
            </p>
          </Section>

          {/* DOs */}
          <Section title="DOs">
            <ul className="list-disc pl-5">
              <li>
                Do check the extent of onboarded resources in the tool (such as
                countries, regulators and time periods) before relying on its
                outputs.
              </li>
            </ul>
          </Section>

          {/* DON'Ts */}
          <Section title="DON'Ts">
            <ul className="list-disc pl-5">
              <li>
                Don't rely on the tool as a complete repository of all
                applicable resources and always consider whether additional
                sources or professional judgement are required.
              </li>
            </ul>
          </Section>
        </div>

        {/* Fixed Footer */}
        <div className="flex justify-end px-8 py-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={handleAccept} className="px-6 py-2 btn-green">
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="mb-4">
    <h3 className="text-base font-semibold title-base-color mb-1">{title}</h3>
    <div className="text-sm text-base-color leading-relaxed">{children}</div>
  </div>
);

export { RulesOfTheRoadModal };
